import { IndianRupee, TrendingUp, Clock, Gift } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { useDeliveryDashboard, useDeliveryEarnings, useDeliveryPartnerId } from "@/hooks/queries";
import type { DeliveryEarningRow } from "@/types/delivery.ui";

const statusBadge: Record<string, string> = {
  paid: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  processing: "bg-info/10 text-info border-info/20",
};

const DeliveryEarningsPage = () => {
  const { partnerId, isLoading: partnerLoading } = useDeliveryPartnerId();
  const { data: stats, isLoading: statsLoading } = useDeliveryDashboard(partnerId);
  const { data: earningsData, isLoading: earningsLoading } = useDeliveryEarnings(partnerId);

  const earnings = earningsData?.items ?? [];
  const isLoading = partnerLoading || statsLoading || earningsLoading;

  const table = useDataTable<DeliveryEarningRow>({
    data: earnings,
    searchKeys: ["period"],
  });

  const columns: DataTableColumn<DeliveryEarningRow>[] = [
    {
      id: "period",
      header: "Period",
      accessorKey: "period",
      sortable: true,
      mobilePriority: 1,
      cell: (e) => <span className="font-semibold">{e.period}</span>,
    },
    {
      id: "deliveries",
      header: "Deliveries",
      accessorKey: "deliveries",
      sortable: true,
      align: "right",
      mobileLabel: "Deliveries",
      mobilePriority: 2,
    },
    {
      id: "totalEarnings",
      header: "Earnings",
      accessorKey: "totalEarnings",
      sortable: true,
      align: "right",
      mobileLabel: "Earnings",
      mobilePriority: 3,
      cell: (e) => <span className="font-medium">₹{e.totalEarnings}</span>,
    },
    {
      id: "tips",
      header: "Tips",
      accessorKey: "tips",
      sortable: true,
      align: "right",
      hideOnMobile: true,
      cell: (e) => `₹${e.tips}`,
    },
    {
      id: "bonuses",
      header: "Bonuses",
      accessorKey: "bonuses",
      sortable: true,
      align: "right",
      hideOnMobile: true,
      cell: (e) => `₹${e.bonuses}`,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 4,
      cell: (e) => (
        <Badge variant="outline" className={statusBadge[e.status]}>{e.status}</Badge>
      ),
    },
    {
      id: "paidDate",
      header: "Paid Date",
      accessorKey: "paidDate",
      hideOnMobile: true,
      className: "text-sm text-muted-foreground",
      cell: (e) => e.paidDate || "—",
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 rounded-xl" />
        <div className="grid gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!partnerId || !stats) {
    return (
      <PageHeader title="Earnings" description="No delivery partner profile linked to your account." />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Earnings"
        description="Track your delivery earnings and payouts."
      />

      <Card className="bg-gradient-accent text-accent-foreground">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-accent-foreground/70 text-sm">Total Earnings</p>
              <p className="text-4xl font-heading font-bold mt-1">₹{stats.totalEarnings.toLocaleString()}</p>
              <p className="text-accent-foreground/60 text-xs mt-2">Pending payout: ₹{stats.pendingSettlement.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <StatsGrid>
        <CompactStatCard title="Today" value={`₹${stats.todayEarnings}`} icon={IndianRupee} />
        <CompactStatCard title="Today's Deliveries" value={stats.todayDeliveries} icon={TrendingUp} />
        <CompactStatCard title="Pending" value={`₹${stats.pendingSettlement.toLocaleString()}`} icon={Clock} />
        <CompactStatCard title="Total Deliveries" value={stats.totalDeliveries} icon={Gift} />
      </StatsGrid>

      <DataTable
        title="Payout History"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search periods..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        hidePagination={earnings.length <= 10}
        renderMobileCard={(e) => (
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-start justify-between">
              <p className="font-semibold text-sm">{e.period}</p>
              <Badge variant="outline" className={statusBadge[e.status]}>{e.status}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-md bg-secondary p-2">
                <p className="font-bold">{e.deliveries}</p>
                <p className="text-muted-foreground">Deliveries</p>
              </div>
              <div className="rounded-md bg-secondary p-2">
                <p className="font-bold">₹{e.totalEarnings}</p>
                <p className="text-muted-foreground">Earnings</p>
              </div>
              <div className="rounded-md bg-secondary p-2">
                <p className="font-bold">₹{e.tips + e.bonuses}</p>
                <p className="text-muted-foreground">Tips+Bonus</p>
              </div>
            </div>
            {e.paidDate && <p className="text-xs text-muted-foreground">Paid: {e.paidDate}</p>}
          </div>
        )}
      />
    </div>
  );
};

export default DeliveryEarningsPage;
